import { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert as MuiAlert,
  Autocomplete,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import BuildIcon from "@mui/icons-material/Build";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { PPAccount } from "../../types/paymentProcessor";
import {
  MerchantMappingConfig,
  MerchantMappingFormData,
  MerchantMappingSaveBody,
  RemapUnmappedResponse,
  PpType,
  DescriptorMatcher,
} from "../../types/merchantMappingConfig";
import { pascalCase } from "../../utils/textUtils";
import { formatPpAccountLabel } from "../../utils/ppAccountLabel";

const PP_ACCOUNT_TYPES: PpType[] = ["shopify", "checkout_champ", "stripe", "crm_connector"];

const emptyFormData: MerchantMappingFormData = {
  merchantName: "",
  descriptors: [],
  descriptorMatcher: "starts_with",
  ppType: "",
  ppAccountId: "",
  isActive: true,
};

const matcherOptions: { value: DescriptorMatcher; label: string }[] = [
  { value: "starts_with", label: "Starts With" },
  { value: "equals", label: "Equals" },
];

interface ConflictInfo {
  merchantName: string;
  config1: MerchantMappingConfig;
  config2: MerchantMappingConfig;
  overlappingDescriptors: string[];
  isWildcard: boolean;
}

function checkDescriptorOverlap(
  desc1: string[],
  matcher1: DescriptorMatcher | null,
  desc2: string[],
  matcher2: DescriptorMatcher | null
): { overlaps: boolean; overlappingDescriptors: string[]; isWildcard: boolean } {
  // Two wildcards → conflict (nondeterministic which one backend picks)
  if (desc1.length === 0 && desc2.length === 0) {
    return { overlaps: true, overlappingDescriptors: [], isWildcard: true };
  }
  // Wildcard + specific descriptors → not a conflict (backend: specific match wins, wildcard is fallback)
  if (desc1.length === 0 || desc2.length === 0) {
    return { overlaps: false, overlappingDescriptors: [], isWildcard: false };
  }

  const overlapping: string[] = [];

  if (matcher1 === "equals" && matcher2 === "equals") {
    for (const d1 of desc1) {
      if (desc2.includes(d1)) overlapping.push(d1);
    }
  } else if (matcher1 === "starts_with" && matcher2 === "starts_with") {
    for (const d1 of desc1) {
      for (const d2 of desc2) {
        if (d1.startsWith(d2) || d2.startsWith(d1)) {
          overlapping.push(d1 === d2 ? d1 : `${d1} / ${d2}`);
        }
      }
    }
  } else {
    const prefixes = matcher1 === "starts_with" ? desc1 : desc2;
    const exacts = matcher1 === "equals" ? desc1 : desc2;
    for (const prefix of prefixes) {
      for (const exact of exacts) {
        if (exact.startsWith(prefix)) overlapping.push(exact);
      }
    }
  }

  return { overlaps: overlapping.length > 0, overlappingDescriptors: overlapping, isWildcard: false };
}

interface SyncFeature {
  onSync: () => Promise<RemapUnmappedResponse>;
  onSyncCompany: (companyId: number) => Promise<RemapUnmappedResponse>;
  onRematchCompany: (companyId: number) => Promise<RemapUnmappedResponse>;
}

export interface MerchantMappingSettingsProps {
  providerLabel: string;
  merchantFieldLabel: string;
  companyId: number | null;
  configs: MerchantMappingConfig[];
  ppAccounts: PPAccount[];
  merchantNameOptions: string[];
  loading: boolean;
  apiError: string | null;
  onCreate: (body: MerchantMappingSaveBody) => Promise<void>;
  onUpdate: (configId: number, body: MerchantMappingSaveBody) => Promise<void>;
  onDelete: (configId: number) => Promise<void>;
  onRefresh: () => Promise<void>;
  syncFeature?: SyncFeature;
  disableDescriptors?: boolean;
}

export default function MerchantMappingSettings({
  providerLabel,
  merchantFieldLabel,
  companyId,
  configs,
  ppAccounts,
  merchantNameOptions,
  loading,
  apiError,
  onCreate,
  onUpdate,
  onDelete,
  onRefresh,
  syncFeature,
  disableDescriptors = false,
}: MerchantMappingSettingsProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<MerchantMappingConfig | null>(null);
  const [deletingConfig, setDeletingConfig] = useState<MerchantMappingConfig | null>(null);
  const [formData, setFormData] = useState<MerchantMappingFormData>(emptyFormData);
  const [descriptorsText, setDescriptorsText] = useState("");
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncMode, setSyncMode] = useState<"global" | "company" | "rematch">("global");
  const [syncResult, setSyncResult] = useState<RemapUnmappedResponse | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const filteredAccounts = ppAccounts.filter(
    (a) =>
      PP_ACCOUNT_TYPES.includes(a.ppType) &&
      (!companyId || Number(a.companyId) === Number(companyId))
  );

  const makeAccountKey = (ppType: string | null, ppAccountId: number | null) =>
    ppType && ppAccountId ? `${ppType}:${ppAccountId}` : "company-only";

  const parseAccountKey = (key: string) => {
    const [ppType, id] = key.split(":");
    return { ppType: ppType as PpType, ppAccountId: Number(id) };
  };

  const selectedAccountKey =
    formData.ppType && formData.ppAccountId
      ? makeAccountKey(formData.ppType, Number(formData.ppAccountId))
      : "";

  const getAccountLabel = (ppAccountId: number | null, ppType: string | null) => {
    if (!ppAccountId || !ppType) return "Company Only";
    const account = ppAccounts.find(
      (a) => a.ppAccountId === ppAccountId && a.ppType === ppType
    );
    if (account) {
      return formatPpAccountLabel(account);
    }
    return `#${ppAccountId}`;
  };

  const conflicts = useMemo(() => {
    const activeConfigs = configs.filter((c) => c.isActive);
    const byMerchant = new Map<string, MerchantMappingConfig[]>();
    for (const config of activeConfigs) {
      const group = byMerchant.get(config.merchantName) || [];
      group.push(config);
      byMerchant.set(config.merchantName, group);
    }

    const results: ConflictInfo[] = [];
    for (const [merchantName, group] of byMerchant) {
      if (group.length < 2) continue;
      for (let i = 0; i < group.length; i++) {
        for (let j = i + 1; j < group.length; j++) {
          const c1 = group[i];
          const c2 = group[j];
          if (makeAccountKey(c1.ppType, c1.ppAccountId) === makeAccountKey(c2.ppType, c2.ppAccountId)) continue;
          const { overlaps, overlappingDescriptors, isWildcard } = checkDescriptorOverlap(
            c1.descriptors || [],
            c1.descriptorMatcher,
            c2.descriptors || [],
            c2.descriptorMatcher
          );
          if (overlaps) {
            results.push({ merchantName, config1: c1, config2: c2, overlappingDescriptors, isWildcard });
          }
        }
      }
    }
    return results;
  }, [configs]);

  // --- Dialog handlers ---

  const parseDescriptorsText = (text: string): string[] =>
    text.split(",").map((s) => s.trim()).filter(Boolean);

  const handleOpenCreate = () => {
    setEditingConfig(null);
    setFormData(emptyFormData);
    setDescriptorsText("");
    setDialogOpen(true);
  };

  const handleOpenEdit = (config: MerchantMappingConfig) => {
    setEditingConfig(config);
    setFormData({
      merchantName: config.merchantName,
      descriptors: config.descriptors || [],
      descriptorMatcher: config.descriptorMatcher || "starts_with",
      ppType: config.ppType || "",
      ppAccountId: config.ppAccountId || "",
      isActive: config.isActive,
    });
    setDescriptorsText(config.descriptors?.join(", ") || "");
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConfig(null);
    setFormData(emptyFormData);
    setDescriptorsText("");
  };

  const handleDialogClose = (_event: unknown, reason: string) => {
    if (reason === "backdropClick") return;
    handleCloseDialog();
  };

  const handleOpenDelete = (config: MerchantMappingConfig) => {
    setDeletingConfig(config);
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteDialogOpen(false);
    setDeletingConfig(null);
  };

  const handleOpenSync = (mode: "global" | "company" | "rematch") => {
    setSyncMode(mode);
    setSyncResult(null);
    setSyncDialogOpen(true);
  };

  const handleCloseSync = () => {
    const hadRemapped = syncResult && syncResult.remapped > 0;
    setSyncDialogOpen(false);
    setSyncResult(null);
    if (hadRemapped) onRefresh();
  };

  const handleSync = async () => {
    if (loading || !syncFeature) return;
    try {
      let result: RemapUnmappedResponse;
      if (syncMode === "rematch" && companyId) {
        result = await syncFeature.onRematchCompany(companyId);
      } else if (syncMode === "company" && companyId) {
        result = await syncFeature.onSyncCompany(companyId);
      } else {
        result = await syncFeature.onSync();
      }
      setSyncResult(result);
    } catch (err) {
      console.error("Failed to remap alerts:", err);
    }
  };

  // --- Form handlers ---

  const handleFormChange = (
    field: keyof MerchantMappingFormData,
    value: any
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isFormValid = () => {
    if (!formData.merchantName.trim()) return false;
    const descriptors = parseDescriptorsText(descriptorsText);
    if (descriptors.length > 0 && !formData.descriptorMatcher) return false;
    return true;
  };

  // --- CRUD operations ---

  const buildSaveBody = (): MerchantMappingSaveBody => {
    const descriptors = parseDescriptorsText(descriptorsText);
    const hasAccount = formData.ppType && formData.ppAccountId;
    return {
      merchantName: formData.merchantName.trim(),
      descriptors,
      descriptorMatcher:
        descriptors.length > 0 ? formData.descriptorMatcher : undefined,
      ppType: hasAccount ? (formData.ppType as PpType) : undefined,
      ppAccountId: hasAccount ? Number(formData.ppAccountId) : undefined,
      companyId: !hasAccount ? (companyId ?? undefined) : undefined,
      isActive: formData.isActive,
    };
  };

  const handleSubmit = async () => {
    if (!isFormValid() || loading) return;

    const body = buildSaveBody();

    try {
      if (editingConfig) {
        await onUpdate(editingConfig.configId, body);
        setSnackbar({
          open: true,
          message: "Config updated successfully",
          severity: "success",
        });
      } else {
        await onCreate(body);
        setSnackbar({
          open: true,
          message: "Config created successfully",
          severity: "success",
        });
      }
      handleCloseDialog();
      await onRefresh();
    } catch (err) {
      console.error("Failed to save config:", err);
    }
  };

  const handleDelete = async () => {
    if (!deletingConfig || loading) return;

    try {
      await onDelete(deletingConfig.configId);
      setSnackbar({
        open: true,
        message: "Config deleted successfully",
        severity: "success",
      });
      handleCloseDelete();
      await onRefresh();
    } catch (err) {
      console.error("Failed to delete config:", err);
    }
  };

  if (!companyId) {
    return (
      <Paper
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
          px: 4,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Please select a company from the header to manage {providerLabel} alert configs.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      {/* Error State - only show at page level when no dialog is open */}
      {(apiError) && !dialogOpen && !deleteDialogOpen && !syncDialogOpen && (
        <Box className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          Error: {apiError}
        </Box>
      )}

      {/* Header with Add button */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="body2" color="text.secondary">
          Manage how {providerLabel} alerts are matched to accounts or companies.
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{
              backgroundColor: '#465fff',
              '&:hover': {
                backgroundColor: '#3641f5',
              },
            }}
          >
            Add
          </Button>
          {syncFeature && (
            <>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={() => handleOpenSync("company")}
              >
                Sync Company Alerts
              </Button>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={() => handleOpenSync("global")}
              >
                Sync All Alerts
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<BuildIcon />}
                onClick={() => handleOpenSync("rematch")}
              >
                Rematch Company Alerts
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Configs Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{providerLabel} Merchant Name</TableCell>
                <TableCell>Descriptors</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Active</TableCell>
                <TableCell sx={{ width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Loading...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No configs found. Click &quot;Add&quot; to create
                      one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                configs.map((config) => (
                  <TableRow
                    key={config.configId}
                    hover
                    sx={{
                      opacity: config.isActive ? 1 : 0.6,
                    }}
                  >
                    <TableCell>{config.merchantName}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          flexWrap: "wrap",
                        }}
                      >
                        {config.descriptorMatcher && (
                          <Typography variant="body2" sx={{ lineHeight: 1 }}>
                            {pascalCase(config.descriptorMatcher)}:
                          </Typography>
                        )}

                        {config.descriptors && config.descriptors.length > 0
                          ? config.descriptors.map((d) => (
                            <Chip key={d} label={d} size="small" />
                          ))
                          : <Chip key="all" label="All" size="small" />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {config.ppType && config.ppAccountId
                        ? `${getAccountLabel(config.ppAccountId, config.ppType)} (${pascalCase(config.ppType)})`
                        : <Typography variant="body2" color="text.secondary" component="span" sx={{ fontStyle: "italic" }}>Company Only</Typography>
                      }
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={config.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={config.isActive ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEdit(config)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDelete(config)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {conflicts.length > 0 && (
        <MuiAlert severity="warning" icon={false} sx={{ mt: 2, border: "1px solid #fef08a", backgroundColor: "#fefce8" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <WarningAmberIcon fontSize="small" sx={{ color: "#d97706" }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Conflicts detected
            </Typography>
          </Box>
          <Box component="ul" sx={{ m: 0, pl: 2, listStyleType: "disc" }}>
            {conflicts.map((conflict, idx) => (
              <li key={idx}>
                <Typography variant="body2">
                  <strong>{conflict.merchantName}</strong>
                  {" — "}
                  {conflict.isWildcard
                    ? "a config matching all descriptors conflicts with another config. "
                    : `overlapping descriptors: ${conflict.overlappingDescriptors.join(", ")}. `}
                  Routes to both{" "}
                  <strong>
                    {conflict.config1.ppType && conflict.config1.ppAccountId
                      ? `${getAccountLabel(conflict.config1.ppAccountId, conflict.config1.ppType)} (${pascalCase(conflict.config1.ppType)})`
                      : "Company Only"}
                  </strong>
                  {" and "}
                  <strong>
                    {conflict.config2.ppType && conflict.config2.ppAccountId
                      ? `${getAccountLabel(conflict.config2.ppAccountId, conflict.config2.ppType)} (${pascalCase(conflict.config2.ppType)})`
                      : "Company Only"}
                  </strong>.
                </Typography>
              </li>
            ))}
          </Box>
        </MuiAlert>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingConfig ? "Edit Mapping" : "New Mapping"}
        </DialogTitle>
        <DialogContent>
          {apiError && (
            <MuiAlert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </MuiAlert>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: 1,
            }}
          >
            <Autocomplete
              freeSolo
              options={merchantNameOptions}
              value={formData.merchantName}
              onInputChange={(_e, value) =>
                handleFormChange("merchantName", value)
              }
              disabled={loading}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={merchantFieldLabel}
                  required
                  autoComplete="off"
                />
              )}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl size="small" sx={{ width: "20%" }}>
                <InputLabel>Matcher</InputLabel>
                <Select
                  value={formData.descriptorMatcher}
                  label="Matcher"
                  onChange={(e: SelectChangeEvent) =>
                    handleFormChange(
                      "descriptorMatcher",
                      e.target.value as DescriptorMatcher
                    )
                  }
                  disabled={loading || parseDescriptorsText(descriptorsText).length === 0}
                >
                  {matcherOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Descriptors"
                value={descriptorsText}
                onChange={(e) => setDescriptorsText(e.target.value)}
                size="small"
                sx={{ width: "80%" }}
                disabled={loading || disableDescriptors}
                helperText="Comma-separated. Leave empty to match all descriptors."
              />
            </Box>

            <FormControl size="small" fullWidth>
              <InputLabel>Account (optional)</InputLabel>
              <Select
                value={selectedAccountKey}
                label="Account (optional)"
                onChange={(e: SelectChangeEvent) => {
                  const value = e.target.value;
                  if (!value) {
                    setFormData((prev) => ({ ...prev, ppType: "", ppAccountId: "" }));
                  } else {
                    const { ppType, ppAccountId } = parseAccountKey(value);
                    setFormData((prev) => ({ ...prev, ppType, ppAccountId }));
                  }
                }}
                disabled={loading}
              >
                <MenuItem value="">
                  <em>None (Company Only)</em>
                </MenuItem>
                {filteredAccounts.map((account) => (
                  <MenuItem
                    key={makeAccountKey(account.ppType, account.ppAccountId)}
                    value={makeAccountKey(
                      account.ppType,
                      account.ppAccountId
                    )}
                  >
                    {formatPpAccountLabel(account)} ({pascalCase(account.ppType)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    handleFormChange("isActive", e.target.checked)
                  }
                  disabled={loading}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid() || loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading
              ? "Saving..."
              : editingConfig
                ? "Update"
                : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDelete}>
        <DialogTitle>Delete Config</DialogTitle>
        <DialogContent>
          {apiError && (
            <MuiAlert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </MuiAlert>
          )}
          <Typography>
            Are you sure you want to delete the config for{" "}
            <strong>{deletingConfig?.merchantName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDelete} color="inherit" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync Confirmation / Results Dialog */}
      {syncFeature && (
        <Dialog open={syncDialogOpen} onClose={(_e, reason) => { if (reason !== "backdropClick") handleCloseSync(); }} maxWidth="sm" fullWidth>
          <DialogTitle>
            {syncMode === "rematch"
              ? "Rematch Company Alerts"
              : syncMode === "company"
                ? "Re-sync Unmapped Company Alerts"
                : "Re-sync All Unmapped Alerts"}
          </DialogTitle>
          <DialogContent>
            {apiError && (
              <MuiAlert severity="error" sx={{ mb: 2 }}>
                {apiError}
              </MuiAlert>
            )}
            {syncResult ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
                <MuiAlert severity={syncResult.remapped > 0 ? "success" : "info"}>
                  {syncResult.remapped > 0
                    ? `Successfully remapped ${syncResult.remapped} alert${syncResult.remapped !== 1 ? "s" : ""}.`
                    : syncMode === "rematch"
                      ? "No alerts needed remapping. All alerts already match the current mapping rules."
                      : "No unmapped alerts could be matched with current mapping rules."}
                </MuiAlert>
                <Box sx={{ display: "flex", gap: 3 }}>
                  <Typography variant="body2"><strong>Total scanned:</strong> {syncResult.total}</Typography>
                  <Typography variant="body2"><strong>Remapped:</strong> {syncResult.remapped}</Typography>
                  <Typography variant="body2"><strong>Still unmapped:</strong> {syncResult.still_unmapped}</Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
                {syncMode === "rematch" ? (
                  <>
                    <MuiAlert severity="warning" sx={{ mb: 0 }}>
                      This re-evaluates <strong>all</strong> alerts for this company against the current mapping rules,
                      including alerts that are already mapped to an account. Alerts whose mapping differs from the
                      current rules will be updated.
                    </MuiAlert>
                    <Typography variant="body2" color="text.secondary">
                      Use this after correcting a mapping rule — for example, when a merchant was mapped to the wrong
                      company or account and the config has been fixed.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Run this against <strong>old (incorrect) company</strong> — alerts still belong to
                      it until they are rematched to the correct company.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2">
                      {syncMode === "company"
                        ? "Scans unmapped alerts for this company and tries to match them using the latest mapping rules."
                        : "Scans all unmapped alerts across all companies and tries to match them using the latest mapping rules."}
                      {" "}Matched alerts will be queued for automation or marked as &quot;Action Needed&quot; depending
                      on the payment processor account configuration.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {syncMode === "company"
                        ? "Use this after a payment processor account mapping has been added for this company."
                        : "Use this after a \"Company Only\" mapping has been added to link previously unmatched alerts to a company."}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            {syncResult ? (
              <Button onClick={handleCloseSync} variant="contained">
                Close
              </Button>
            ) : (
              <>
                <Button onClick={handleCloseSync} color="inherit" disabled={loading}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSync}
                  variant="contained"
                  disabled={loading}
                  color={syncMode === "rematch" ? "warning" : "primary"}
                  startIcon={loading ? <CircularProgress size={16} /> : syncMode === "rematch" ? <BuildIcon /> : <SyncIcon />}
                >
                  {loading ? "Processing..." : syncMode === "rematch" ? "Rematch" : "Sync"}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
}
